// src/components/admin/CarryOverConfirmModal.tsx
// ============================================================================
// DEVİR ONAY MODALI — "sonraki aya aktar" için sistem içi onay penceresi
// ============================================================================
// Tarayıcının window.confirm kutusu yerine kullanılır: panelin kendi görünümü,
// tutarın nereye taşınacağı ve para transferi OLMADIĞI net yazar.
// Hem kullanıcı kazancı (AdminEarningsSection) hem satış ortaklığı
// (AdminAffiliateSection) ekranı aynı bileşeni kullanır — kopya yok.
// ============================================================================

import React from 'react';
import { ArrowRightCircle, X, Loader2, Info } from 'lucide-react';

interface CarryOverConfirmModalProps {
  /** Kimin dönemi devrediliyor */
  userName: string;
  userEmail?: string;
  /** Devredilen dönem, ör. "Mart 2026" */
  periodName: string;
  /** Devredilecek tutar (formatlanmış, ör. "$14,00") */
  amountText: string;
  /** Asgari ödeme tutarı (formatlanmış) — neden devredilebildiğini açıklar */
  minPayoutText?: string;
  /** "Kullanıcı kazancı" / "Satış ortaklığı komisyonu" ayrımı için etiket */
  kindLabel?: string;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const CarryOverConfirmModal: React.FC<CarryOverConfirmModalProps> = ({
  userName,
  userEmail,
  periodName,
  amountText,
  minPayoutText,
  kindLabel = 'Kullanıcı kazancı',
  loading = false,
  onCancel,
  onConfirm,
}) => (
  <div
    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    onClick={loading ? undefined : onCancel}
  >
    <div
      className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Başlık */}
      <div className="border-b p-4 flex items-center justify-between bg-gradient-to-r from-sky-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
            <ArrowRightCircle className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">Sonraki Aya Devret</h3>
            <p className="text-xs text-gray-500">{kindLabel}</p>
          </div>
        </div>
        <button
          onClick={onCancel}
          disabled={loading}
          className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Kim / hangi dönem */}
        <div className="p-3 border rounded-xl">
          <p className="font-semibold text-gray-800">{userName}</p>
          {userEmail && <p className="text-xs text-gray-500">{userEmail}</p>}
          <div className="flex justify-between mt-2 text-sm">
            <span className="text-gray-600">Dönem</span>
            <span className="font-medium text-gray-800">{periodName}</span>
          </div>
        </div>

        {/* Devredilecek tutar */}
        <div className="p-4 bg-sky-50 border border-sky-200 rounded-xl text-center">
          <p className="text-sm text-gray-600">Devredilecek tutar</p>
          <p className="text-3xl font-bold text-sky-700">{amountText}</p>
          {minPayoutText && (
            <p className="text-xs text-gray-500 mt-1">
              Asgari ödeme tutarının ({minPayoutText}) altında
            </p>
          )}
        </div>

        {/* Ne olacağının açık anlatımı */}
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-gray-700 space-y-1">
              <p><strong>Para transfer edilmez.</strong> Bu dönemin ödemesi ertelenir.</p>
              <p>Tutar bir sonraki dönemin ödemesine eklenir; ikisi tek seferde ödenir.</p>
              <p className="text-gray-500">Kullanıcı panelinde “sonraki döneme devredildi” olarak görünür — kazancı kaybolmaz.</p>
            </div>
          </div>
        </div>

        {/* Aksiyonlar */}
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Vazgeç
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Devrediliyor...
              </>
            ) : (
              <>
                <ArrowRightCircle className="w-4 h-4" />
                Devret
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default CarryOverConfirmModal;
